import rest from '../Utils/rest';

/**
 * Upload a public asset (image or video) to S3
 * 
 * @param {Object} params - Parameters for the API call
 * @param {Array} params.files - The files to upload
 * @param {string} [params.description] - Optional description of the asset
 * @param {string} [params.presentationName] - Optional display name for the asset
 * @param {Function} [params.onProgress] - Optional callback for upload progress
 * @returns {Promise<Object>} - Promise that resolves to the uploaded asset data
 */
export const uploadPublicAssetAPI = ({files, description, presentationName, onProgress}) => {
    const data = {};
    if (description) data.description = description;
    if (presentationName) data.presentationName = presentationName;

    return rest.uploadFiles('api/sa/publicAssets/upload', files, onProgress, data);
};

/**
 * Get public assets with pagination and filtering
 * Available for all logged-in users
 * 
 * @param {Object} params - Parameters for the API call
 * @param {Object} params.paginationModel - Pagination parameters (page, pageSize)
 * @param {Object} params.filterModel - Filter parameters (filterValues)
 * @param {Object} params.sortModel - Sort parameters
 * @returns {Promise<Object>} - Promise that resolves to the API response
 */
export const getPublicAssetsAPI = ({paginationModel, filterModel, sortModel}) => {
    return rest.jsonPostUrl('/api/publicAssets', {paginationModel, filterModel, sortModel});
};




/**
 * Uploads an image specifically for the image component in the WeezBee course editor
 * when in super admin mode (for public templates)
 * 
 * @param {Object} params - Parameters for the API call
 * @param {Array} params.files - The files to upload
 * @param {string} params.templateId - The ID of the template
 * @param {string} params.sectionId - The ID of the section containing the image component
 * @param {string} params.componentId - The ID of the image component to update
 * @param {Function} [params.onProgress] - Optional callback for upload progress
 * @returns {Promise<Object>} - Promise that resolves to the uploaded image data and updated template
 */
export const SA_uploadImageComponentAPI = ({files, templateId, sectionId, componentId, onProgress}) => {
    // Use the public assets upload endpoint instead of the template-specific one
    // This ensures the asset is uploaded to the public assets bank
    return rest.uploadFiles(`api/sa/publicAssets/upload`, files, onProgress, {
        presentationName: `Template Image (${templateId})`,
        description: `Image for template ${templateId}, section ${sectionId}, component ${componentId}`,
        // Include the template, section, and component IDs for reference
        templateId,
        sectionId,
        componentId
    });
};

/**
 * Uploads a video specifically for the video component in the WeezBee course editor
 * when in super admin mode (for public templates)
 * 
 * @param {Object} params - Parameters for the API call
 * @param {Array} params.files - The files to upload
 * @param {string} params.templateId - The ID of the template
 * @param {string} params.sectionId - The ID of the section containing the video component
 * @param {string} params.componentId - The ID of the video component to update
 * @param {Function} [params.onProgress] - Optional callback for upload progress
 * @returns {Promise<Object>} - Promise that resolves to the uploaded video data and updated template
 */
export const SA_uploadVideoComponentAPI = ({files, templateId, sectionId, componentId, onProgress}) => {
    // Use the public assets upload endpoint instead of the template-specific one
    // This ensures the asset is uploaded to the public assets bank
    return rest.uploadFiles(`api/sa/publicAssets/upload`, files, onProgress, {
        presentationName: `Template Video (${templateId})`,
        description: `Video for template ${templateId}, section ${sectionId}, component ${componentId}`,
        // Include the template, section, and component IDs for reference
        templateId,
        sectionId,
        componentId
    });
};
